// 신청·문의관리: 문의관리 / 신청관리 / SaaS 상품관리
//  - 문의/신청: 접수 목록(마스터) ↔ 상세(1:1 스레드/답변/상태/첨부/다운로드)
//  - 상품관리: 플랫폼(boxs/chanbi/gnote)별 SaaS 상품 등록/수정/삭제
import { useEffect, useRef, useState } from 'preact/hooks';
import type { SaasProduct, RequestSummary, RequestDetail, Platform, RequestKind } from '@shared/requests';
import { PLATFORMS, PLATFORM_LABELS, KIND_LABELS, statusesFor } from '@shared/requests';
import { api, ApiError } from '../core/ApiClient';
import { statusClass } from '../core/statusColor';
import { ConfirmDialog } from './ConfirmDialog';
import { ReadButton } from './ReadButton';

const PAGE_SIZE = 10;
const wonFmt = (n: number): string => (n ? Number(n).toLocaleString('ko-KR') : '');

function StatusSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <select
      class={`status-select ${statusClass(value)}`}
      value={value}
      onChange={(e) => onChange((e.target as HTMLSelectElement).value)}
      onClick={(e) => e.stopPropagation()}
    >
      {options.map((o) => (
        <option value={o}>{o}</option>
      ))}
    </select>
  );
}

function Pager({ page, total, onPage }: { page: number; total: number; onPage: (p: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pages <= 1) return null;
  return (
    <div class="pager">
      <button class="btn btn--icon" disabled={page <= 0} onClick={() => onPage(page - 1)}>
        ◀
      </button>
      <span class="pager__info">
        {page + 1} / {pages}
      </span>
      <button class="btn btn--icon" disabled={page >= pages - 1} onClick={() => onPage(page + 1)}>
        ▶
      </button>
    </div>
  );
}

type Tab = 'inquiry' | 'application' | 'products';

export function RequestsView({
  initialKind,
  initialRequestId,
}: {
  /** 알림 딥링크로 지정된 접수 종류 탭 */
  initialKind?: RequestKind;
  /** 알림 딥링크로 바로 열 접수 id */
  initialRequestId?: number | null;
} = {}) {
  const [tab, setTab] = useState<Tab>(initialKind ?? 'inquiry');
  return (
    <div class="manage">
      <div class="manage__tabs">
        <button class={`tab ${tab === 'inquiry' ? 'active' : ''}`} onClick={() => setTab('inquiry')}>
          문의관리
        </button>
        <button class={`tab ${tab === 'application' ? 'active' : ''}`} onClick={() => setTab('application')}>
          신청관리
        </button>
        <button class={`tab ${tab === 'products' ? 'active' : ''}`} onClick={() => setTab('products')}>
          상품관리
        </button>
      </div>

      {tab === 'products' ? (
        <ProductManager />
      ) : (
        <RequestManager
          kind={tab}
          key={tab}
          initialRequestId={initialKind === tab ? (initialRequestId ?? null) : null}
        />
      )}
    </div>
  );
}

// ---- 접수(문의/신청) 관리 ----
function RequestManager({ kind, initialRequestId }: { kind: RequestKind; initialRequestId?: number | null }) {
  const [list, setList] = useState<RequestSummary[]>([]);
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [platformFilter, setPlatformFilter] = useState<'' | Platform>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState<{ msg: string; fn: () => void } | null>(null);

  const statuses = statusesFor(kind);

  const guard = async (fn: () => Promise<void>) => {
    setErr('');
    try {
      await fn();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '오류가 발생했습니다.');
    }
  };

  const load = () => guard(async () => setList(await api.listRequests({ kind })));
  const openedInitial = useRef(false);
  useEffect(() => {
    void load();
    // 알림 딥링크로 지정된 접수는 최초 1회 상세로 바로 연다.
    if (initialRequestId && !openedInitial.current) {
      openedInitial.current = true;
      void select(initialRequestId);
    }
  }, [kind]);

  const q = search.trim().toLowerCase();
  const filtered = list.filter(
    (r) =>
      (!platformFilter || r.platform === platformFilter) &&
      (!statusFilter || r.status === statusFilter) &&
      (!q || r.churchName.toLowerCase().includes(q) || r.title.toLowerCase().includes(q)),
  );
  const paged = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const select = (id: number) => guard(async () => setDetail(await api.getRequest(id)));

  const changeStatus = (r: RequestSummary | RequestDetail, status: string) =>
    guard(async () => {
      // 상태변경 = 새 버전(서버가 읽음수 1로 초기화, updatedAt 갱신). 상세도 동기화.
      const summary = await api.updateRequestStatus(r.id, status);
      await load();
      if (detail?.id === r.id) setDetail({ ...detail, ...summary });
    });

  const sendReply = () =>
    guard(async () => {
      if (!detail) return;
      if (!reply.trim() && files.length === 0) return;
      const updated = await api.replyRequest(detail.id, reply.trim(), files);
      setDetail(updated);
      setReply('');
      setFiles([]);
      await load();
    });

  const removeAttachment = (aid: number) =>
    setConfirm({
      msg: '첨부파일을 삭제하시겠습니까?',
      fn: () =>
        guard(async () => {
          await api.deleteRequestAttachment(aid);
          if (detail) setDetail(await api.getRequest(detail.id));
        }),
    });

  const removeRequest = (r: RequestSummary | RequestDetail) =>
    setConfirm({
      msg: `'${r.churchName || r.title}' ${KIND_LABELS[kind]} 내역을 삭제하시겠습니까? (첨부 포함, 복구 불가)`,
      fn: () =>
        guard(async () => {
          await api.deleteRequest(r.id);
          if (detail?.id === r.id) setDetail(null);
          await load();
        }),
    });

  return (
    <>
      {err && <div class="msg-error" style="margin-bottom:12px">{err}</div>}
      <div class="manage__grid">
        {/* 접수 목록 */}
        <div class="manage__col">
          <div class="manage__col-head">
            {KIND_LABELS[kind]} 접수 ({filtered.length})
          </div>
          <div class="row" style="margin-bottom:8px;gap:6px">
            <select
              class="input"
              value={platformFilter}
              onChange={(e) => {
                setPlatformFilter((e.target as HTMLSelectElement).value as '' | Platform);
                setPage(0);
              }}
            >
              <option value="">전체 플랫폼</option>
              {PLATFORMS.map((p) => (
                <option value={p}>{PLATFORM_LABELS[p]}</option>
              ))}
            </select>
            <select
              class="input"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter((e.target as HTMLSelectElement).value);
                setPage(0);
              }}
            >
              <option value="">전체 상태</option>
              {statuses.map((s) => (
                <option value={s}>{s}</option>
              ))}
            </select>
          </div>
          <input
            class="input"
            style="margin-bottom:10px"
            placeholder="🔍 교회명 · 제목 검색"
            value={search}
            onInput={(e) => {
              setSearch((e.target as HTMLInputElement).value);
              setPage(0);
            }}
          />
          <div class="manage__list">
            {paged.map((r) => (
              <div
                key={r.id}
                class={`co-card ${detail?.id === r.id ? 'active' : ''}`}
                onClick={() => select(r.id)}
              >
                <div class="co-card__top">
                  <span class="co-card__name">{r.churchName || '(무기명)'}</span>
                  <span class={`badge2 ${statusClass(r.status)}`}>{r.status}</span>
                </div>
                <div class="co-card__meta" style="font-size:13px;color:var(--text-muted,#666)">
                  {r.title}
                </div>
                <div class="badge-row">
                  <span class="badge2 st-info">{PLATFORM_LABELS[r.platform]}</span>
                  {r.productName && <span class="badge2 st-neutral">{r.productName}</span>}
                  <span class="badge2 st-neutral">👁 {r.readCount}</span>
                  {r.attachmentCount > 0 && <span class="badge2 st-neutral">📎 {r.attachmentCount}</span>}
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <div class="empty" style="padding:16px">
                {list.length === 0 ? `${KIND_LABELS[kind]} 접수가 없습니다.` : '검색 결과가 없습니다.'}
              </div>
            )}
          </div>
          <Pager page={page} total={filtered.length} onPage={setPage} />
        </div>

        {/* 상세: 정보 + 스레드 + 답변 */}
        <div class="manage__col manage__col--wide">
          {!detail ? (
            <div class="empty" style="padding:40px">
              {KIND_LABELS[kind]} 내역을 선택하세요.
            </div>
          ) : (
            <div>
              <div class="detail-head">
                <h3 style="margin:0">{detail.title}</h3>
                <div class="spacer" />
                <ReadButton
                  kind="request"
                  id={detail.id}
                  version={detail.updatedAt}
                  count={detail.readCount}
                  onIncrement={() => api.markRequestRead(detail.id)}
                  onCounted={(readCount) => {
                    setDetail({ ...detail, readCount });
                    setList((prev) => prev.map((r) => (r.id === detail.id ? { ...r, readCount } : r)));
                  }}
                />
                <StatusSelect
                  value={detail.status}
                  options={statuses}
                  onChange={(v) => changeStatus(detail, v)}
                />
                <button class="btn btn--danger" onClick={() => removeRequest(detail)}>
                  삭제
                </button>
              </div>

              <div class="badge-row" style="margin-bottom:10px">
                <span class="badge2 st-info">{PLATFORM_LABELS[detail.platform]}</span>
                <span class="badge2 st-neutral">{KIND_LABELS[detail.kind]}</span>
                {detail.productName && <span class="badge2 st-neutral">상품: {detail.productName}</span>}
              </div>

              <div class="field-grid">
                <div class="req-info">
                  <b>교회명</b> {detail.churchName || '-'}
                </div>
                <div class="req-info">
                  <b>담당자</b> {detail.contactName || '-'}
                </div>
                <div class="req-info">
                  <b>연락처</b> {detail.contactPhone || '-'}
                </div>
                <div class="req-info">
                  <b>이메일</b> {detail.contactEmail || '-'}
                </div>
                <div class="req-info">
                  <b>접수출처</b> {detail.source || '-'}
                </div>
                <div class="req-info">
                  <b>접수일</b> {detail.createdAt}
                </div>
              </div>

              {/* 1:1 스레드 */}
              <div class="manage__col-head" style="margin-top:18px">
                1:1 스레드 ({detail.messages.length})
              </div>
              <div class="thread">
                {detail.messages.map((m) => (
                  <div key={m.id} class={`msg msg--${m.sender}`}>
                    <div class="msg__head">
                      <span class="msg__who">{m.sender === 'admin' ? '관리자' : '교회'}</span>
                      <span class="msg__date">{m.createdAt}</span>
                    </div>
                    {m.content && <div class="msg__body">{m.content}</div>}
                    {m.attachments.length > 0 && (
                      <div class="attach-list" style="margin-top:6px">
                        {m.attachments.map((a) => (
                          <span key={a.id} class="attach-chip">
                            <a href={a.url} target="_blank" rel="noreferrer">
                              📎 {a.originalName}
                            </a>
                            <button
                              class="attach-chip__del"
                              title="첨부 삭제"
                              onClick={() => removeAttachment(a.id)}
                            >
                              ✕
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                {detail.messages.length === 0 && (
                  <div class="empty" style="padding:12px">메시지가 없습니다.</div>
                )}
              </div>

              {/* 답변 입력 */}
              <div class="reply-box">
                <textarea
                  class="input memo-area"
                  placeholder="답변 내용을 입력하세요"
                  value={reply}
                  onInput={(e) => setReply((e.target as HTMLTextAreaElement).value)}
                />
                <div class="row" style="margin-top:8px;gap:8px">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setFiles(Array.from((e.target as HTMLInputElement).files ?? []))}
                  />
                  <div class="spacer" />
                  <button class="btn btn--primary" onClick={sendReply}>
                    답변 등록
                  </button>
                </div>
                {files.length > 0 && (
                  <div class="co-card__meta" style="margin-top:4px">
                    첨부 {files.length}개 선택됨
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {confirm && (
        <ConfirmDialog
          title="확인"
          message={confirm.msg}
          confirmLabel="삭제"
          danger
          onConfirm={() => {
            const fn = confirm.fn;
            setConfirm(null);
            fn();
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

// ---- SaaS 상품 관리 ----
function ProductManager() {
  const [platform, setPlatform] = useState<Platform>('boxs');
  const [products, setProducts] = useState<SaasProduct[]>([]);
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [desc, setDesc] = useState('');
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState<{ msg: string; fn: () => void } | null>(null);

  const guard = async (fn: () => Promise<void>) => {
    setErr('');
    try {
      await fn();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '오류가 발생했습니다.');
    }
  };

  const load = () => guard(async () => setProducts(await api.listProducts(platform)));
  useEffect(() => {
    void load();
  }, [platform]);

  const add = () =>
    guard(async () => {
      const n = name.trim();
      if (!n) return;
      await api.createProduct({
        platform,
        name: n,
        price: Number(price.replace(/[^0-9]/g, '')) || 0,
        description: desc.trim(),
      });
      setName('');
      setPrice('');
      setDesc('');
      await load();
    });

  const patch = (p: SaasProduct, body: Partial<SaasProduct>) =>
    guard(async () => {
      await api.updateProduct(p.id, body as never);
      await load();
    });

  const remove = (p: SaasProduct) =>
    setConfirm({
      msg: `상품 '${p.name}' 을(를) 삭제하시겠습니까?`,
      fn: () =>
        guard(async () => {
          await api.deleteProduct(p.id);
          await load();
        }),
    });

  return (
    <>
      {err && <div class="msg-error" style="margin-bottom:12px">{err}</div>}
      <div class="manage__tabs" style="margin-bottom:14px">
        {PLATFORMS.map((p) => (
          <button class={`tab ${platform === p ? 'active' : ''}`} onClick={() => setPlatform(p)}>
            {PLATFORM_LABELS[p]}
          </button>
        ))}
      </div>

      <div class="manage__col" style="max-width:820px">
        <div class="manage__col-head">{PLATFORM_LABELS[platform]} 상품 ({products.length})</div>

        {/* 신규 등록 */}
        <div class="field-grid" style="margin-bottom:8px">
          <input
            class="input"
            placeholder="상품명"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => e.key === 'Enter' && add()}
          />
          <input
            class="input"
            inputMode="numeric"
            placeholder="가격(원)"
            value={price}
            onInput={(e) => {
              const d = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
              setPrice(d ? Number(d).toLocaleString('ko-KR') : '');
            }}
          />
        </div>
        <div class="row" style="margin-bottom:14px;gap:8px">
          <input
            class="input"
            placeholder="설명(선택)"
            value={desc}
            onInput={(e) => setDesc((e.target as HTMLInputElement).value)}
          />
          <button class="btn btn--primary btn--add" onClick={add}>
            등록
          </button>
        </div>

        {products.map((p) => (
          <ProductRow key={p.id} product={p} onPatch={patch} onRemove={remove} />
        ))}
        {products.length === 0 && <div class="empty" style="padding:16px">등록된 상품이 없습니다.</div>}
      </div>

      {confirm && (
        <ConfirmDialog
          title="삭제 확인"
          message={confirm.msg}
          confirmLabel="삭제"
          danger
          onConfirm={() => {
            const fn = confirm.fn;
            setConfirm(null);
            fn();
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
    </>
  );
}

function ProductRow({
  product,
  onPatch,
  onRemove,
}: {
  product: SaasProduct;
  onPatch: (p: SaasProduct, body: Partial<SaasProduct>) => void;
  onRemove: (p: SaasProduct) => void;
}) {
  const [name, setName] = useState(product.name);
  const [price, setPrice] = useState(wonFmt(product.price));
  const [desc, setDesc] = useState(product.description);
  useEffect(() => {
    setName(product.name);
    setPrice(wonFmt(product.price));
    setDesc(product.description);
  }, [product.id]);

  return (
    <div class="site-card" style="padding:10px 12px;margin-bottom:8px">
      <div class="field-grid">
        <label>
          상품명
          <input
            class="input"
            value={name}
            onInput={(e) => setName((e.target as HTMLInputElement).value)}
            onBlur={() => name.trim() && name !== product.name && onPatch(product, { name: name.trim() })}
          />
        </label>
        <label>
          가격(원)
          <input
            class="input"
            inputMode="numeric"
            value={price}
            onInput={(e) => {
              const d = (e.target as HTMLInputElement).value.replace(/[^0-9]/g, '');
              setPrice(d ? Number(d).toLocaleString('ko-KR') : '');
            }}
            onBlur={() => {
              const n = Number(price.replace(/[^0-9]/g, '')) || 0;
              if (n !== product.price) onPatch(product, { price: n });
            }}
          />
        </label>
      </div>
      <label class="memo-field">
        설명
        <input
          class="input"
          value={desc}
          onInput={(e) => setDesc((e.target as HTMLInputElement).value)}
          onBlur={() => desc !== product.description && onPatch(product, { description: desc })}
        />
      </label>
      <div class="row" style="margin-top:8px;gap:10px">
        <label class="row" style="gap:6px;cursor:pointer">
          <input
            type="checkbox"
            checked={product.isActive}
            onChange={(e) => onPatch(product, { isActive: (e.target as HTMLInputElement).checked })}
          />
          노출(활성)
        </label>
        <div class="spacer" />
        <button class="btn btn--icon btn--danger" onClick={() => onRemove(product)}>
          ✕
        </button>
      </div>
    </div>
  );
}
